import { AnalyticsScreenConstants, ScenariosEventTypes } from '@buxus/shared'
import { trackEvent } from '../platform/analytics/analytics'

export const SCENARIOS_SCREEN = 'SCENARIOS_SCREEN' as const

export type ScenariosListKind = 'sessions' | 'templates'

export type ScenariosPaneId =
  | 'inspect'
  | 'settings'
  | 'people'
  | 'chat'
  | 'timeline'
  | 'profile'
  | 'help'
  | 'map_details'

export type ScenariosModalId =
  | 'run_options'
  | 'run_countdown'
  | 'stop_run_confirm'
  | 'go_live'
  | 'version_duplicate'
  | 'get_started'
  | 'share'
  | 'invite'
  | 'session_delete'
  | 'object_delete'
  | 'roster_remove'

export type ScenariosDismissMethod = 'escape' | 'cancel' | 'backdrop' | 'close_button'

export type ScenariosShortcutId =
  | 'focus'
  | 'scribble'
  | 'eraseScribble'
  | 'palette'
  | 'plumes'
  | 'runRoomPlumes'
  | 'pointerShare'
  | 'autoTrack'
  | 'boardReveal'
  | 'debugPanels'
  | 'screenCapture'
  | 'keys'

export type ScenariosAnalyticsContext = {
  sessionId?: string | null
  sessionType?: string | null
  status?: 'draft' | 'published' | 'live' | 'done' | null
  sessionTenantId?: string | null
  currentTenantId?: string | null
  isOnRoster?: boolean
  isCreator?: boolean
  isLinkGuest?: boolean
  canEditBoard?: boolean
  canOperateSession?: boolean
  canManagePeople?: boolean
  viewingCount?: number
  viewingAsUserId?: string | null
  viewingAsEmail?: string | null
  list?: ScenariosListKind
  invitedCount?: number
  liveCount?: number
  joinedCount?: number
  draftCount?: number
  publishedCount?: number
  doneCount?: number
  interestCount?: number
  oversightCount?: number
  templateCount?: number
  takeNumber?: number
}

let latestCtx: ScenariosAnalyticsContext | null = null

export function setScenariosAnalyticsContext(ctx: ScenariosAnalyticsContext): void {
  latestCtx = ctx
}

export function scenariosAnalyticsContext(): ScenariosAnalyticsContext | null {
  return latestCtx
}

function omitEmpty(value: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, raw] of Object.entries(value)) {
    if (raw === undefined || raw === null || raw === '') continue
    out[key] = raw
  }
  return out
}

export function scenariosBaseProps(
  extra?: Record<string, unknown>,
  ctx: ScenariosAnalyticsContext | null | undefined = latestCtx,
): Record<string, unknown> {
  const sessionTenantId = ctx?.sessionTenantId ?? null
  const currentTenantId = ctx?.currentTenantId ?? null
  const target =
    sessionTenantId &&
    currentTenantId &&
    sessionTenantId.toLowerCase() !== currentTenantId.toLowerCase()
      ? { targetTenantId: sessionTenantId }
      : {}
  return omitEmpty({
    isScenarios: true,
    screen: AnalyticsScreenConstants.SCENARIOS_SCREEN,
    screenConstant: SCENARIOS_SCREEN,
    sessionId: ctx?.sessionId,
    sessionType: ctx?.sessionType,
    status: ctx?.status,
    sessionTenantId,
    isOnRoster: ctx?.isOnRoster,
    isCreator: ctx?.isCreator,
    isLinkGuest: ctx?.isLinkGuest,
    canEditBoard: ctx?.canEditBoard,
    canOperateSession: ctx?.canOperateSession,
    canManagePeople: ctx?.canManagePeople,
    viewingCount: ctx?.viewingCount,
    viewingAsUserId: ctx?.viewingAsUserId,
    viewingAsEmail: ctx?.viewingAsEmail,
    list: ctx?.list,
    invitedCount: ctx?.invitedCount,
    liveCount: ctx?.liveCount,
    joinedCount: ctx?.joinedCount,
    draftCount: ctx?.draftCount,
    publishedCount: ctx?.publishedCount,
    doneCount: ctx?.doneCount,
    interestCount: ctx?.interestCount,
    oversightCount: ctx?.oversightCount,
    templateCount: ctx?.templateCount,
    takeNumber: ctx?.takeNumber,
    ...target,
    ...extra,
  })
}

export function trackScenariosEvent(
  name: string,
  extra?: Record<string, unknown>,
): void {
  trackEvent(name, scenariosBaseProps(extra))
}

export function trackScenariosPaneOpened(pane: ScenariosPaneId | null): void {
  trackScenariosEvent(ScenariosEventTypes.PANE_OPENED, { pane })
}

export function trackScenariosModalDismissed(
  modal: ScenariosModalId,
  dismissMethod: ScenariosDismissMethod,
): void {
  trackScenariosEvent(ScenariosEventTypes.MODAL_DISMISSED, { modal, dismissMethod })
}

export function trackScenariosShortcut(
  shortcut: ScenariosShortcutId,
  source: 'keyboard' | 'ui',
  snapshot: Record<string, unknown>,
): void {
  trackScenariosEvent(ScenariosEventTypes.SHORTCUT_USED, {
    shortcut,
    source,
    ...snapshot,
  })
}

export function dockTabToPane(dock: string): ScenariosPaneId | null {
  switch (dock) {
    case 'timeline':
      return 'timeline'
    case 'inspector':
      return 'inspect'
    case 'notes':
      return 'chat'
    case 'people':
      return 'people'
    case 'settings':
      return 'settings'
    case 'account':
      return 'profile'
    default:
      return null
  }
}
